import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Polygon,
  Marker,
  Tooltip,
  Popup,
  useMap,
  ZoomControl,
  Circle,
} from "react-leaflet";
import {
  ParkingArea,
  ParkingSlot,
  ParkingType,
  SlotStatus,
  VehicleDefect,
} from "../../types";
import { HOTEL_LOCATION, vehicles } from "../../mockData";
import L, { Map } from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom marker icons
import hotelIcon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// Define marker icons
const HotelIcon = L.icon({
  iconUrl: hotelIcon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

interface ParkingMapProps {
  parkingAreas: ParkingArea[];
  showSlots: boolean;
  selectedArea: string | null;
  onAreaSelect: (areaId: string) => void;
  onSlotSelect?: (slotId: string) => void;
}

// Custom component to set the view to the selected area
const ChangeView = ({
  center,
  zoom,
}: {
  center: [number, number];
  zoom: number;
}) => {
  const map = useMap();

  useEffect(() => {
    try {
      // console.log('Setting view to:', center, zoom);
      map.setView(center, zoom);
    } catch (error) {
      console.error("Error setting map view:", error);
    }
  }, [center, zoom, map]);

  return null;
};

const ParkingMap: React.FC<ParkingMapProps> = ({
  parkingAreas,
  showSlots,
  selectedArea,
  onAreaSelect,
  onSlotSelect,
}) => {
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Determine the center and zoom level
  let center: [number, number] = HOTEL_LOCATION; // Default to hotel location

  if (selectedArea) {
    const selectedAreaData = parkingAreas.find(
      (area) => area.id === selectedArea
    );
    if (
      selectedAreaData &&
      selectedAreaData.center &&
      selectedAreaData.center[0] !== undefined &&
      selectedAreaData.center[1] !== undefined &&
      !isNaN(selectedAreaData.center[0]) &&
      !isNaN(selectedAreaData.center[1])
    ) {
      center = selectedAreaData.center;
    }
  }

  const zoom = selectedArea ? 19 : 17;

  // Get color based on parking type
  const getParkingTypeColor = (type: ParkingType) => {
    switch (type) {
      case ParkingType.RENTED:
        return "#10B981";
      case ParkingType.PUBLIC:
        return "#F59E0B";
      case ParkingType.DEDICATED:
        return "#3B82F6";
      default:
        return "#6B7280";
    }
  };

  const mapRef = useRef<Map>();
  const resizeObserverRef = React.useRef<ResizeObserver>(null);

  useEffect(() => {
    const resizeObserver = resizeObserverRef.current;
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Get color based on slot status
  const getSlotStatusColor = (slot: ParkingSlot) => {
    return slot.status === SlotStatus.AVAILABLE ? "#22C55E" : "#EF4444";
  };

  // Calculate occupancy rate for an area
  const getOccupancyRate = (area: ParkingArea) => {
    return Math.round((area.occupied / area.capacity) * 100);
  };

  // Get CSS class for the occupancy rate
  const getOccupancyClass = (rate: number) => {
    if (rate >= 0.9) return "text-red-500";
    if (rate >= 0.7) return "text-yellow-500";
    return "text-green-500";
  };

  return (
    <div className="space-y-4">
      {/* Area selection panel */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {parkingAreas.map((area) => (
          <div
            key={area.id}
            className={`p-3 rounded-lg bg-background cursor-pointer transition-all duration-200 shadow-sm border-2 ${
              selectedArea === area.id
                ? "border-accent shadow-md"
                : "border-gray-200 hover:border-gray-300"
            }`}
            onClick={() => onAreaSelect(area.id)}
          >
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-sm text-contentColor">
                {area.name}
              </h3>
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${getOccupancyClass(
                  getOccupancyRate(area)
                )}`}
              >
                {getOccupancyRate(area)}%
              </span>
            </div>
            <div className="flex items-center mt-1 gap-2 text-xs text-gray-500">
              <span
                style={{
                  backgroundColor: getParkingTypeColor(area.type),
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  display: "inline-block",
                }}
              ></span>
              <span>{area.type}</span>
              <span>·</span>
              <span>{area.distance} km</span>
            </div>
            <div className="mt-1 text-xs">
              <span className="text-contentColor">
                {area.occupied}/{area.capacity} occupied
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Map */}
      <div className="w-full h-[600px] rounded-lg shadow-lg z-10">
        {/* <MapContainer
          center={center}
          zoom={zoom}
          // className="w-full h-[600px] rounded-lg shadow-lg z-10"
          zoomControl={false}
        > */}

        <MapContainer
          ref={(ref) => {
            if (!ref) return;
            mapRef.current = ref;

            const container = document.getElementById("map");
            if (!container) return;

            L.DomEvent.disableClickPropagation(
              container
            ).disableScrollPropagation(container);

            const observer = new ResizeObserver(() => {
              if (!ref) {
                return;
              }
              if (ref.getContainer()) {
                ref?.invalidateSize({ pan: false });
              }
            });
            if (container) {
              observer.observe(container);
            }
          }}
          id="map"
          center={center}
          zoom={zoom}
          zoomSnap={0.45}
          style={{
            width: "100%",
            height: "600px",
            marginTop: "6px",
            borderRadius: "6px",
            zIndex: 0,
          }}
        >
          <ChangeView center={center} zoom={zoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ZoomControl position="bottomright" />

          {/* Hotel marker */}
          <Marker position={HOTEL_LOCATION} icon={HotelIcon}>
            <Tooltip permanent>
              <div className="font-semibold">IHL Hotel</div>
            </Tooltip>
            <Popup>
              <div className="text-center">
                <h3 className="font-bold text-lg">IHL Hotel</h3>
                <p className="text-gray-600">Your luxury stay in Mumbai</p>
              </div>
            </Popup>
          </Marker>

          {/* Hotel radius indicator */}
          <Circle
            center={HOTEL_LOCATION}
            radius={500}
            pathOptions={{
              color: "#3B82F6",
              fillColor: "#3B82F6",
              fillOpacity: 0.05,
              weight: 1,
              dashArray: "5,5",
            }}
          />

          {/* Parking areas */}
          {parkingAreas.map((area) => (
            <React.Fragment key={area.id}>
              <Polygon
                positions={area.geofence}
                pathOptions={{
                  color: getParkingTypeColor(area.type),
                  fillColor: getParkingTypeColor(area.type),
                  fillOpacity:
                    hoveredArea === area.id || selectedArea === area.id
                      ? 0.6
                      : 0.3,
                  weight:
                    selectedArea === area.id
                      ? 3
                      : hoveredArea === area.id
                      ? 2
                      : 1,
                }}
                eventHandlers={{
                  click: () => onAreaSelect(area.id),
                  mouseover: () => setHoveredArea(area.id),
                  mouseout: () => setHoveredArea(null),
                }}
              >
                <Tooltip sticky>
                  <div className="p-1">
                    <div className="font-bold text-lg">{area.name}</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
                      <div className="text-gray-600">Type:</div>
                      <div className="font-medium">{area.type}</div>

                      <div className="text-gray-600">Occupancy:</div>
                      <div className="font-medium">
                        <span
                          className={getOccupancyClass(getOccupancyRate(area))}
                        >
                          {area.occupied}/{area.capacity} (
                          {getOccupancyRate(area)}%)
                        </span>
                      </div>

                      <div className="text-gray-600">Distance:</div>
                      <div className="font-medium">{area.distance} km</div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Click to {selectedArea === area.id ? "toggle" : "view"}{" "}
                      slots
                    </div>
                  </div>
                </Tooltip>
              </Polygon>

              {/* Area label */}
              <Marker
                position={area.center}
                icon={L.divIcon({
                  className: "custom-div-icon",
                  html: `<div style="background-color: white; padding: 3px 6px; border-radius: 4px; font-weight: bold; font-size: 12px; border: 1px solid ${getParkingTypeColor(
                    area.type
                  )};">${area.name}</div>`,
                  iconSize: [100, 20],
                  iconAnchor: [50, 10],
                })}
                opacity={selectedArea === area.id && showSlots ? 0 : 1}
              />

              {/* Display individual slots when showSlots is true and area is selected */}
              {showSlots &&
                selectedArea === area.id &&
                area.slots.map((slot) => (
                  <Polygon
                    key={slot.id}
                    positions={slot.geofence}
                    pathOptions={{
                      color: getSlotStatusColor(slot),
                      fillColor: getSlotStatusColor(slot),
                      fillOpacity:
                        selectedSlot === slot.id
                          ? 0.9
                          : hoveredSlot === slot.id
                          ? 0.8
                          : 0.6,
                      weight:
                        selectedSlot === slot.id
                          ? 2
                          : hoveredSlot === slot.id
                          ? 1.5
                          : 1,
                    }}
                    eventHandlers={{
                      click: () => {
                        setSelectedSlot(
                          selectedSlot === slot.id ? null : slot.id
                        );
                        if (onSlotSelect) onSlotSelect(slot.id);
                      },
                      mouseover: () => setHoveredSlot(slot.id),
                      mouseout: () => setHoveredSlot(null),
                    }}
                  >
                    <Tooltip>
                      <div className="p-1">
                        <div className="font-bold">{slot.name}</div>
                        <div className="mt-1 grid grid-cols-2 gap-x-3">
                          <div className="text-gray-600">Status:</div>
                          <div
                            className={`font-medium ${
                              slot.status === SlotStatus.AVAILABLE
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {slot.status}
                          </div>
                          <div className="text-gray-600">Type:</div>
                          <div className="font-medium">{slot.type}</div>
                        </div>
                        {slot.status === SlotStatus.OCCUPIED && (
                          <div className="mt-2 text-xs text-gray-500">
                            Click to view vehicle details
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </Polygon>
                ))}
            </React.Fragment>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="bg-background p-4 rounded-lg shadow-sm border border-gray-200">
        <h3 className="font-bold mb-2 text-contentColor">Map Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-blue-500"></span>
            <span className="text-sm text-contentColor">Dedicated</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-emerald-500"></span>
            <span className="text-sm text-contentColor">Rented</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-amber-500"></span>
            <span className="text-sm text-contentColor">Public</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-red-500"></span>
            <span className="text-sm text-contentColor">Occupied</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-green-500"></span>
            <span className="text-sm text-contentColor">Available</span>
          </div>
        </div>
      </div>

      {/* Selected slot and vehicle info */}
      {selectedSlot && (
        <div className="bg-background p-4 rounded-lg shadow-md border border-gray-200">
          {(() => {
            // Find the slot
            const slot = parkingAreas
              .flatMap((area) => area.slots)
              .find((s) => s.id === selectedSlot);

            if (!slot) return <p>No slot data found</p>;

            // Find the vehicle if slot is occupied
            const vehicle =
              slot.status === SlotStatus.OCCUPIED
                ? vehicles.find((v) => v.slotId === slot.id)
                : null;

            return (
              <div>
                <div className="flex justify-between items-start">
                  <h3 className="font-bold text-lg">Slot {slot.name}</h3>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      slot.status === SlotStatus.AVAILABLE
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {slot.status}
                  </span>
                </div>

                {vehicle ? (
                  <div className="mt-4">
                    <div className="bg-background p-3 rounded-lg">
                      <h4 className="font-semibold text-md">
                        Vehicle Information
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                        <div>
                          <p className="text-xs text-gray-500">License Plate</p>
                          <p className="font-semibold">
                            {vehicle.licensePlate}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Make & Model</p>
                          <p>
                            {vehicle.make} {vehicle.model}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Year</p>
                          <p>{vehicle.year}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Color</p>
                          <p>{vehicle.color}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="font-semibold text-md">
                          Parking Details
                        </h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
                          <p className="text-xs text-gray-500">Check-in Time</p>
                          <p>
                            {new Date(vehicle.checkInTime).toLocaleString()}
                          </p>

                          <p className="text-xs text-gray-500">
                            Expected Duration
                          </p>
                          <p>{vehicle.expectedDuration} hours</p>

                          <p className="text-xs text-gray-500">
                            Current Charges
                          </p>
                          <p className="font-semibold">
                            ₹{vehicle.charges?.total || 0}
                          </p>

                          <p className="text-xs text-gray-500">
                            Payment Status
                          </p>
                          <p
                            className={
                              vehicle.charges?.paid
                                ? "text-green-600"
                                : "text-amber-600"
                            }
                          >
                            {vehicle.charges?.paid ? "Paid" : "Pending"}
                          </p>

                          <p className="text-xs text-gray-500">Handled By</p>
                          <p>{vehicle.handledBy}</p>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="font-semibold text-md">
                          Customer Details
                        </h4>
                        {vehicle.customerDetails && (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
                            <p className="text-xs text-gray-500">Name</p>
                            <p>{vehicle.customerDetails.name}</p>

                            <p className="text-xs text-gray-500">Contact</p>
                            <p>{vehicle.customerDetails.contact}</p>

                            {vehicle.customerDetails.email && (
                              <>
                                <p className="text-xs text-gray-500">Email</p>
                                <p>{vehicle.customerDetails.email}</p>
                              </>
                            )}

                            {vehicle.customerDetails.isGuest && (
                              <>
                                <p className="text-xs text-gray-500">
                                  Room Number
                                </p>
                                <p>{vehicle.customerDetails.roomNumber}</p>

                                <p className="text-xs text-gray-500">
                                  Guest Status
                                </p>
                                <p className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Hotel Guest
                                </p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vehicle Defects */}
                    {vehicle.defects && vehicle.defects.length > 0 && (
                      <div className="mt-4 bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <h4 className="font-semibold text-md">
                            Vehicle Defects
                          </h4>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {vehicle.defects.length}{" "}
                            {vehicle.defects.length === 1 ? "Issue" : "Issues"}
                          </span>
                        </div>

                        <div className="mt-3 space-y-2">
                          {vehicle.defects.map((defect: VehicleDefect) => (
                            <div
                              key={defect.id}
                              className="border-l-4 pl-3 py-1"
                              style={{
                                borderColor:
                                  defect.severity === "SEVERE"
                                    ? "#EF4444"
                                    : defect.severity === "MODERATE"
                                    ? "#F59E0B"
                                    : "#10B981",
                              }}
                            >
                              <div className="flex justify-between">
                                <p className="font-medium">
                                  {defect.description}
                                </p>
                                <span
                                  className={`text-xs px-1 py-0.5 rounded ${
                                    defect.severity === "SEVERE"
                                      ? "bg-red-100 text-red-800"
                                      : defect.severity === "MODERATE"
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-green-100 text-green-800"
                                  }`}
                                >
                                  {defect.severity}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">
                                Location: {defect.location}
                              </p>
                              {defect.notes && (
                                <p className="text-xs italic text-gray-500 mt-1">
                                  {defect.notes}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex justify-end gap-2">
                      <button className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
                        Check Out
                      </button>
                      <button className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300">
                        Edit Details
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <p className="text-center text-gray-600 py-4">
                      This slot is available for parking.
                    </p>
                    <div className="mt-2 text-center">
                      <button className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
                        Register New Vehicle
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ParkingMap;
